<template>

<div class="main list-view no-sub-header">

	<div class="filters">

		<label>Year</label>
		<select v-model="filters.report_year" @change="setPeriod()">
			<option value="">Select...</option>
			<option v-for="(year, index) in years" :value="year.code" :disabled="minDate.substring(0,4) > 	 year.code">{{ year.description }}</option>
		</select>

		<label>Period</label>
		<select v-model="filters.period" @change="setPeriod()">
			<option value="">Select...</option>
			<optgroup label="Quarter">
				<option v-for="(quarter, index) in quarters" :value="quarter.code" :disabled="filters.report_year == getThisYear() ? getThisQuarter() < index + 1 : false">{{ quarter.description }}</option>
			</optgroup>
			<optgroup label="Month">
				<option v-for="(month, index) in months" :value="month.code" :disabled="filters.report_year == getThisYear() ? getThisMonth() < month.code : false">{{ month.description }}</option>
			</optgroup>
		</select>

		<label>Date</label>
		<input type="date" v-model="filters.date" :min="minDate" :max="maxDate" @change="setDate()">

		<div v-if="hasFilters">
			<div class="divider"></div>
			<select v-if="filters.department_id && categories.length > 0" v-model="filters.category_id" @change="getList()">
				<option value="">Category...</option>
				<option v-for="(item, index) in categories" :value="item.category_id">{{ item.description }}</option>
			</select>
			<input type="search" v-model="filters.search_term" placeholder="UPC Search..." @search="getList()">
		</div>

		<div class="divider"></div>
		<div class="note" style="margin: 1em 0;">Select a year and period <b><i>or</i></b> a specific date to view inventory snapshot.</div>
		<div :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="getList()">Run Report</div>
		<div :class="['button', hasFilters ? 'neutral' : 'disabled']"  @click="clearFilters()">Clear Filters</div>
		<div v-if="filteredList" class="button neutral" @click="backToDepartments()">Back To Departments</div>

		<div class="divider"></div>
		<div :class="['extra-menu-item', gettingExport || list.length < 1 ? 'disabled' : '']" @click="doExport()">Export CSV</div>
		<div :class="['extra-menu-item', list.length < 1 ? 'disabled' : '']" @click="printWindow()">Print Report</div>
		<div v-if="getDevEnvironment" :class="['extra-menu-item', list.length < 1 ? 'disabled' : '']" @click="getList()">Reload Report</div>

		<div v-if="getDevEnvironment" class="extra-menu-item" @click="getDates()">Get Dates</div>

	</div>

	<div style="margin: 0 1em;">

		<div v-if="loading" class="loading"></div>

		<div v-if="!hasFilters" class="empty-list" style="margin: 3em 0;">Select a year and period <b><i>or</i></b> a specific date<br>to view inventory snapshot.</div>

		<div v-else-if="filteredList" id="list_wrapper" class="list report-list no-sub-header">
			<div class="printable-heading">Inventory Snapshot, {{ filteredList }}: {{ printHeading }}</div>
			<h3>{{ filteredList }}</h3>
			<table ref="list" id="list_table" class="data-list" cellpadding="0" cellspacing="0">
				<tr class="list_row_totals no-print">
					<td class="list left"></td>
					<td class="list left">TOTALS</td>
					<td class="list right">{{ getColumnTotal('quantity') }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost'), getColumnTotal('price')) }}%</td>
				</tr>
				<tr>
					<th class="list left">Product</th>
					<th class="list left">UPC</th>
					<th class="list right">Items<br>On Hand</th>
					<th class="list right">On Hand<br>Cost</th>
					<th class="list right">On Hand<br>Retail</th>
					<th class="list right">On Hand<br>Margin</th>
				</tr>
				<tr v-for="(item, index) in productList" class="clickable-list-item" @click="goProduct(item)">
					<td class="list left">{{ item.description }}</td>
					<td class="list left">{{ item.upc }}</td>
					<td class="list right">{{ item.quantity || '0' }}</td>
					<td class="list right">{{ $formatCurrency(item.cost) }}</td>
					<td class="list right">{{ $formatCurrency(item.price) }}</td>
					<td class="list right">{{ getMargin(item.cost, item.price) }}%</td>
				</tr>
				<tr class="list_row_totals">
					<td class="list left">TOTALS</td>
					<td class="list left"></td>
					<td class="list right">{{ getColumnTotal('quantity') }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost'), getColumnTotal('price')) }}%</td>
				</tr>
			</table>
		</div>

		<div v-else id="list_wrapper" class="list report-list no-sub-header">
			<div class="printable-heading">Inventory Snapshot: {{ printHeading }}</div>
			<table ref="list" id="list_table" class="data-list" cellpadding="0" cellspacing="0">
				<tr class="list_row_totals no-print">
					<td class="list left">TOTALS</td>
					<td class="list right">{{ getColumnTotal('quantity') }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost'), getColumnTotal('price')) }}%</td>
				</tr>
				<tr>
					<th class="list">Department</th>
					<th class="list right">Items<br>On Hand</th>
					<th class="list right">On Hand<br>Cost</th>
					<th class="list right">On Hand<br>Retail</th>
					<th class="list right">On Hand<br>Margin</th>
				</tr>
				<tr v-for="(item, index) in list" class="clickable-list-item" @click="getDepartment(item.department_id)">
					<td class="list left" style="max-width: 120px;">{{ item.description }}</td>
					<td class="list right">{{ item.quantity || '0' }}</td>
					<td class="list right">{{ $formatCurrency(item.cost) }}</td>
					<td class="list right">{{ $formatCurrency(item.price) }}</td>
					<td class="list right">{{ getMargin(item.cost, item.price) }}%</td>
				</tr>
				<tr class="list_row_totals">
					<td class="list left">TOTALS</td>
					<td class="list right">{{ getColumnTotal('quantity') }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost'), getColumnTotal('price')) }}%</td>
				</tr>
			</table>
		</div>
	
	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{230:'Inventory Management Report', 231: 'Filtering Inventory Management Report'}" :tours="false"></help-popup>

</div>

</template>

<script>
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'inventory-management',
	components: {
		'help-popup': HelpPopup
	},
	data() {
		return {
			loading: false,
			minDate: '',
			maxDate: '',
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			page: 1,
			showPagination: false,
			filters: {},
			filterMessage: '',
			filterLoading: false,
			filteredList: '',
			list: [],
			overrides: {},
			productList: [],
			paging: {},
			manufacturers: [],
			categories: [],
			showDetail: false,
			exportMessage: '',
			gettingExport: false,
			legacyLink: '',
			clientTypeCode: '',
			years: [
				{ code: '2024', description: '2024'},
				{ code: '2023', description: '2023'}
			],
			quarters: [
				{ code: 'first_quarter', description: 'First Quarter'},
				{ code: 'second_quarter', description: 'Second Quarter'},
				{ code: 'third_quarter', description: 'Third Quarter'},
				{ code: 'fourth_quarter', description: 'Fourth Quarter'}
			],
			months: [
				{ code: '01', description: 'January'},
				{ code: '02', description: 'February'},
				{ code: '03', description: 'March'},
				{ code: '04', description: 'April'},
				{ code: '05', description: 'May'},
				{ code: '06', description: 'June'},
				{ code: '07', description: 'July'},
				{ code: '08', description: 'August'},
				{ code: '09', description: 'September'},
				{ code: '10', description: 'October'},
				{ code: '11', description: 'November'},
				{ code: '12', description: 'December'}
			]
		}
	},
	computed: {
		hasFilters() {
			return (this.filters.report_year && this.filters.period) || this.filters.date
		},
		printHeading() {
			// find element in quarters where code = filters.period
			if (this.filters.date) {
				return this.filters.date
			} else if (this.filters.period.indexOf('quarter') > -1) {
				return this.quarters.find((element) => element.code == this.filters.period).description + ' ' + this.filters.report_year
			} else {
				return this.months.find((element) => element.code == this.filters.period).description + ' ' + this.filters.report_year
			}
		}
	},
	created() {
		// this.getDates()
		this.clearFilters()
	},
	methods: {
		getToday() {
			let date = new Date()
			return date.getFullYear() + '-' + String(date.getMonth()+1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0')
		},
		getYesterday() {
			let today = new Date()
			let yesterday = new Date(today)
			return yesterday.setDate(yesterday.getDate() - 1)
		},
		getThisMonth() {
			let date = new Date()
			return String(date.getMonth()+1).padStart(2, '0')
		},
		getThisQuarter() {
			let date = new Date()
			return Math.ceil((date.getMonth()+1) / 3)
		},
		getThisYear() {
			let date = new Date()
			return date.getFullYear()
		},
		getDates() {
			let apiURL = ''
			if (this.getDevEnvironment && document.location.hostname.indexOf('cdssandbox') == -1) {
				apiURL = 'https://compassapi.test/api.php'
			} else {
				apiURL = 'https://app.nssncompass.com/api.php'
			}
			this.apiCall({ destination: apiURL + '?action=get_report_inventory_snapshot_dates' }).then(obj => {
				this.minDate = obj.min_date
				this.maxDate = obj.max_date
			}).catch(err => {
				console.log(err)
			})
		},
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				report_year: '',
				period: '',
				date: '',
				manufacturer_id: '',
				department_id: '',
				category_id: '',
				do_export: false
			}
			this.filteredList = ''
			this.productList = []
			this.list = []
			this.minDate = '2023-01-01'
			let yesterday = ( d => new Date(d.setDate(d.getDate()-1)) )(new Date);
			this.maxDate = yesterday.getFullYear() + '-' + String(yesterday.getMonth()+1).padStart(2, '0') + '-' + String(yesterday.getDate()).padStart(2, '0')
		},
		setPeriod() {
			this.filters.date = ''
			this.getList()
		},
		setDate() {
			this.filters.report_year = ''
			this.filters.period = ''
			this.getList()
		},
		getList() {
			if (this.loading) { return }
			if (!this.hasFilters) { return }
			this.loading = true
			if (this.filters.do_export) {
				this.message = 'Generating CSV for ' + this.list.length + ' items...<p><i>this could take a few moments</i></p>'
			} else {
				this.message = 'Generating report...<p><i>this could take a few moments</i></p>'
			}
			let apiURL = ''
			if (this.getDevEnvironment && document.location.hostname.indexOf('cdssandbox') == -1) {
				apiURL = 'https://compassapi.test/api.php'
			} else {
				apiURL = 'https://app.nssncompass.com/api.php'
			}
			this.apiCall({ destination: apiURL + '?action=get_report_inventory_snapshot', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.filters.do_export = 0
					this.message = 'Downloading CSV...'
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
						this.message = ''
					}, 500)
				} else {
					if (this.filters.search_term) {
						this.productList = obj.list
						this.filteredList = this.filters.search_term
					} else if (this.filters.category_id) {
						this.productList = obj.list
						this.filteredList = obj.category
					} else if (this.filters.department_id) {
						this.productList = obj.list
						this.categories = obj.categories
						this.filteredList = obj.department
					} else {
						this.filteredList = ''
						this.productList = []
						this.list = obj.list
					}
					setTimeout(() => {
						this.message = ''
						// document.getElementById('list_table').scroll({ top: 0, behavior: 'smooth' })
					}, 500)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getDepartment(departmentId) {
			this.filters.department_id = departmentId
			this.filters.category_id = ''
			this.filters.search_term = ''
			this.getList()
		},
		goProduct(item) {
			if (!item.product_id) { return }
			this.$router.push('/inventory/manage-products/detail/' + item.product_id + '?return=/reports/inventory-management')
		},
		backToDepartments() {
			this.filteredList = ''
			this.filters.search_term = ''
			this.filters.category_id = ''
			this.filters.department_id = ''
			this.getList()
		},
		printWindow() {
			window.print()
		},
		doExport() {
			this.filters.do_export = true
			if (this.filters.department_id) {
				this.getDepartment(this.filters.department_id)
			} else {
				this.getList()
			}
		},
		getColumnTotal(column) {
			let total = 0
			if (this.filteredList) {
				this.productList.forEach((product) => {
					if (product[column]) {
						total += parseFloat(product[column])
					}
				})
			} else {
				this.list.forEach((department) => {
					if (department[column]) {
						total += parseFloat(department[column])
					}
				})
			}
			return total
		},
		getMargin(cost, price) {
			if (price > 0) {
				return (100 * ( 1 - ( cost / price) )).toFixed(1)
			} else {
				return 0
			}
		}
	}
}
</script>
